import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import { api } from '../../services/api';

interface Instrument {
  id: number;
  symbol: string;
  qty: number | null;
  sell: number;
  buy: number;
  state: boolean;
}

interface InstrumentsResponse {
  success: boolean;
  data: Instrument[];
}

const InstrumentsPage: React.FC = () => {
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<number | null>(null);

  const fetchInstruments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get<InstrumentsResponse>('/api/v1/admin-panel/instruments');
      
      if (response.data.success) {
        setInstruments(response.data.data);
      } else {
        setError('Failed to load instruments');
      }
    } catch (err: any) {
      console.error('Error fetching instruments:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load instruments');
    } finally {
      setLoading(false);
    }
  };

  const handleStateChange = async (id: number, newState: boolean) => {
    try {
      setUpdating(id);
      setError(null);
      
      await api.post('/api/v1/admin-panel/instruments', {
        id,
        state: newState
      });
      
      // Update the local state
      setInstruments(prev => 
        prev.map(instrument => 
          instrument.id === id 
            ? { ...instrument, state: newState }
            : instrument
        )
      );
    } catch (err: any) {
      console.error('Error updating instrument state:', err);
      setError(err.response?.data?.message || err.message || 'Failed to update instrument state');
    } finally {
      setUpdating(null);
    }
  };

  useEffect(() => {
    fetchInstruments();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchInstruments, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const activeInstruments = instruments.filter(i => i.state).length;
  const totalInstruments = instruments.length;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Торговые инструменты
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Всего инструментов
              </Typography>
              <Typography variant="h4">
                {totalInstruments}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Активных инструментов
              </Typography>
              <Typography variant="h4" color="primary">
                {activeInstruments}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Отключенных инструментов
              </Typography>
              <Typography variant="h4" color="error">
                {totalInstruments - activeInstruments}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Instruments Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Символ</TableCell>
                <TableCell align="right">Размер лота</TableCell>
                <TableCell align="right">Продажа лоты</TableCell>
                <TableCell align="right">Покупка лоты</TableCell>
                <TableCell align="center">Статус</TableCell>
                <TableCell align="center">Действие</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {instruments.map((instrument) => (
                <TableRow key={instrument.id}>
                  <TableCell>{instrument.id}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {instrument.symbol}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {instrument.qty !== null ? instrument.qty.toString() : 'Не указан'}
                  </TableCell>
                  <TableCell align="right">
                    {instrument.sell.toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    {instrument.buy.toLocaleString()}
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={instrument.state ? 'Включен' : 'Отключен'}
                      color={instrument.state ? 'success' : 'error'}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={instrument.state}
                      onChange={(e) => handleStateChange(instrument.id, e.target.checked)}
                      disabled={updating === instrument.id}
                      color="primary"
                    />
                    {updating === instrument.id && (
                      <CircularProgress size={20} sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {instruments.length === 0 && !loading && (
          <Box p={3} textAlign="center">
            <Typography color="textSecondary">
              Нет доступных торговых инструментов
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default InstrumentsPage;
